import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ApiAuthError,
  fetchAdoptionSeries,
  fetchModelDistribution,
  fetchOverview,
  fetchTokenSeries,
  fetchTopSkills,
  fetchTopUsers,
  type DashboardWindow,
} from '../api/client';
import { SignInScreen } from '../auth/SignInScreen';
import { useToken } from '../auth/TokenContext';
import { AdoptionLineChart } from '../components/charts/AdoptionLineChart';
import { ModelDistributionDonut } from '../components/charts/ModelDistributionDonut';
import { TokenStackedAreaChart } from '../components/charts/TokenStackedAreaChart';
import { TopSkillsBarChart } from '../components/charts/TopSkillsBarChart';
import { TopUsersBarChart } from '../components/charts/TopUsersBarChart';
import { KpiCard } from '../components/KpiCard';
import { MonthChipStrip } from '../components/MonthChipStrip';

const TOP_USERS_LIMIT = 10;
const TOP_SKILLS_LIMIT = 10;
/**
 * Earliest month we have data for. Duplicated in
 * `llm-monitoring-worker/src/dashboard/handlers.ts` as MONTHLY_MIN_MONTH —
 * keep them in sync.
 */
const MIN_MONTH = '2026-04';
const QUERY_KEYS = [
  'month-overview',
  'month-adoption',
  'month-token-series',
  'month-top-users',
  'month-top-skills',
  'month-model-distribution',
];

const YEAR_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function isValidYearMonth(raw: string | undefined): raw is string {
  return typeof raw === 'string' && YEAR_MONTH_RE.test(raw);
}

function currentYearMonth(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthWindow(yearMonth: string): { from: string; to: string } {
  const start = new Date(`${yearMonth}-01T00:00:00Z`);
  const next = new Date(start.getTime());
  next.setUTCMonth(next.getUTCMonth() + 1);
  return { from: start.toISOString(), to: next.toISOString() };
}

/**
 * Inclusive range of 'YYYY-MM' month labels from `from` to `to`. When
 * `from > to` returns an empty array. Used to drive the chip strip from
 * MIN_MONTH up to the current month.
 */
function monthRange(from: string, to: string): string[] {
  if (from > to) return [];
  const out: string[] = [];
  const cursor = new Date(`${from}-01T00:00:00Z`);
  const end = new Date(`${to}-01T00:00:00Z`);
  while (cursor.getTime() <= end.getTime()) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}`);
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}

/** Clamp 'YYYY-MM' to the [MIN_MONTH, currentYearMonth()] interval. */
function clampMonth(month: string): string {
  const max = currentYearMonth();
  if (month < MIN_MONTH) return MIN_MONTH;
  if (month > max) return max;
  return month;
}

function humanMonthLabel(yearMonth: string): string {
  return new Date(`${yearMonth}-01T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatUsd(n: number): string {
  if (n >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(0)}%`;
}

export function MonthlyDetail(): JSX.Element {
  const { yearMonth: rawYearMonth } = useParams<{ yearMonth: string }>();
  const parsed = isValidYearMonth(rawYearMonth) ? rawYearMonth : currentYearMonth();
  const yearMonth = clampMonth(parsed);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, signOut } = useToken();
  const tokenKey = token?.slice(-8);

  // Redirect the URL if the requested month was out of range. Effect runs
  // after render so React Router has navigation context.
  useEffect(() => {
    if (yearMonth !== rawYearMonth) {
      navigate(`/monthly/${yearMonth}`, { replace: true });
    }
  }, [yearMonth, rawYearMonth, navigate]);

  const { from, to } = monthWindow(yearMonth);
  // The Month Detail page deliberately ignores the global origin filter —
  // management wants the full picture per month, not a filtered slice.
  const window: DashboardWindow = { from, to, origin: 'all' };
  const maxMonth = currentYearMonth();
  const chips = monthRange(MIN_MONTH, maxMonth);

  const overviewQuery = useQuery({
    queryKey: ['month-overview', yearMonth, tokenKey],
    queryFn: () => fetchOverview(window, token),
    enabled: !!token,
  });
  const adoptionQuery = useQuery({
    queryKey: ['month-adoption', yearMonth, tokenKey],
    queryFn: () => fetchAdoptionSeries(window, token),
    enabled: !!token,
  });
  const tokenSeriesQuery = useQuery({
    queryKey: ['month-token-series', yearMonth, tokenKey],
    queryFn: () => fetchTokenSeries(window, token),
    enabled: !!token,
  });
  const topUsersQuery = useQuery({
    queryKey: ['month-top-users', yearMonth, TOP_USERS_LIMIT, tokenKey],
    queryFn: () => fetchTopUsers(window, TOP_USERS_LIMIT, token),
    enabled: !!token,
  });
  const topSkillsQuery = useQuery({
    queryKey: ['month-top-skills', yearMonth, TOP_SKILLS_LIMIT, tokenKey],
    queryFn: () => fetchTopSkills(window, TOP_SKILLS_LIMIT, token),
    enabled: !!token,
  });
  const modelDistributionQuery = useQuery({
    queryKey: ['month-model-distribution', yearMonth, tokenKey],
    queryFn: () => fetchModelDistribution(window, token),
    enabled: !!token,
  });

  const allQueries = [
    overviewQuery,
    adoptionQuery,
    tokenSeriesQuery,
    topUsersQuery,
    topSkillsQuery,
    modelDistributionQuery,
  ];

  const authError = allQueries
    .map((q) => q.error)
    .find((e): e is ApiAuthError => e instanceof ApiAuthError);

  if (authError) {
    return (
      <SignInScreen
        reason={`Token rejected by the worker (HTTP ${authError.status}): ${authError.message}`}
      />
    );
  }

  const overview = overviewQuery.data;
  const isOverviewLoading = overviewQuery.isLoading;
  const isRefreshing = allQueries.some((q) => q.isFetching);

  return (
    <section className="overview">
      <div className="overview-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            <Link to="/monthly" className="month-back-link">
              ← Monthly
            </Link>{' '}
            <span style={{ color: 'var(--text-muted)' }}>/</span>{' '}
            {humanMonthLabel(yearMonth)}
          </h2>
          <p className="chart-subtitle" style={{ margin: '4px 0 0' }}>
            All metrics scoped to {yearMonth} (UTC). Compared against the previous
            calendar month (same length).
          </p>
        </div>
        <button
          type="button"
          className="refresh-button"
          disabled={isRefreshing}
          onClick={() => {
            for (const key of QUERY_KEYS) {
              queryClient.invalidateQueries({ queryKey: [key] });
            }
          }}
          title="Refresh now"
        >
          {isRefreshing ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
      </div>

      <MonthChipStrip
        selected={yearMonth}
        months={chips}
        minMonth={MIN_MONTH}
        maxMonth={maxMonth}
        onSelect={(m) => navigate(`/monthly/${clampMonth(m)}`)}
      />

      {allQueries.some((q) => q.isError) && !authError && (
        <div className="overview-error">
          Failed to load some data.{' '}
          <button type="button" onClick={signOut}>Sign out</button>
        </div>
      )}

      <div className="kpi-strip">
        <KpiCard
          label="Total cost"
          value={overview ? formatUsd(overview.current.totalCostUsd) : ''}
          delta={overview?.deltas.totalCostUsd ?? null}
          invertColors
          loading={isOverviewLoading}
          hint="USD across all models this month"
        />
        <KpiCard
          label="Active users"
          value={overview ? formatNumber(overview.current.activeUsers) : ''}
          delta={overview?.deltas.activeUsers ?? null}
          loading={isOverviewLoading}
          hint="Distinct user_id"
        />
        <KpiCard
          label="Sessions"
          value={overview ? formatNumber(overview.current.sessions) : ''}
          delta={overview?.deltas.sessions ?? null}
          loading={isOverviewLoading}
          hint="Cone-aggregated sessions"
        />
        <KpiCard
          label="Completion rate"
          value={overview ? formatPercent(overview.current.completionRate) : ''}
          delta={overview?.deltas.completionRate ?? null}
          loading={isOverviewLoading}
          hint="v5 outcome=completed / classified"
        />
        <KpiCard
          label="Completed sessions"
          value={overview ? formatNumber(overview.current.completedSessionCount) : ''}
          delta={overview?.deltas.completedSessionCount ?? null}
          loading={isOverviewLoading}
          hint="v5 outcome=completed"
        />
        <KpiCard
          label="Cost / completed session"
          value={overview ? formatUsd(overview.current.costPerCompletedSessionUsd) : ''}
          delta={overview?.deltas.costPerCompletedSessionUsd ?? null}
          invertColors
          loading={isOverviewLoading}
          hint="cost / completedSessions"
        />
      </div>

      <section className="chart-section">
        <header>
          <h2>Daily active users</h2>
          <p className="chart-subtitle">Distinct users per UTC day in {yearMonth}.</p>
        </header>
        <AdoptionLineChart
          points={adoptionQuery.data?.points ?? []}
          loading={adoptionQuery.isLoading}
        />
      </section>

      <section className="chart-section">
        <header>
          <h2>Daily token usage by category</h2>
          <p className="chart-subtitle">
            Stacked tokens per UTC day in {yearMonth}. Hover any day for the breakdown
            plus an estimated USD cost.
          </p>
        </header>
        <TokenStackedAreaChart
          points={tokenSeriesQuery.data?.points ?? []}
          loading={tokenSeriesQuery.isLoading}
        />
      </section>

      <div className="chart-row-2col">
        <section className="chart-section">
          <header>
            <h2>Top users by tokens</h2>
            <p className="chart-subtitle">
              Top {TOP_USERS_LIMIT} users in {yearMonth}, ranked by total tokens (input
              + output + cache).
            </p>
          </header>
          <TopUsersBarChart
            users={topUsersQuery.data?.users ?? []}
            loading={topUsersQuery.isLoading}
          />
        </section>

        <section className="chart-section">
          <header>
            <h2>Model distribution</h2>
            <p className="chart-subtitle">
              Share of total tokens per LLM model in {yearMonth}.
            </p>
          </header>
          <ModelDistributionDonut
            models={modelDistributionQuery.data?.models ?? []}
            loading={modelDistributionQuery.isLoading}
          />
        </section>
      </div>

      <section className="chart-section">
        <header>
          <h2>Top skills loaded</h2>
          <p className="chart-subtitle">
            Top {TOP_SKILLS_LIMIT} skills loaded in {yearMonth}, ranked by load count.
          </p>
        </header>
        <TopSkillsBarChart
          skills={topSkillsQuery.data?.skills ?? []}
          loading={topSkillsQuery.isLoading}
        />
      </section>
    </section>
  );
}
